// ============================================================================
// ActivityEventsTable — the granular, filterable event feed (ORG-USAGE-2026)
//
// One row per metered request: when, who, which SYSTEM ran (versionless
// station name), Work Units, timing, and cost. Filters live in one row above
// the table. Cursor-paginated so large organizations stay fast.
//
// 2026-08-13 WU migration: no raw token counts render anywhere on this
// surface. The single usage number is PRO Work Units, derived client-side
// from the user-charged USD the backend already returns (WU = usd / 2.18) —
// the same derivation the chat ⓘ popup uses, so the two reconcile (R18).
// ============================================================================

import { PREST0N_OWN_WORK_FILTER, isPersonsOwnTurn } from '../../config/activityKindView';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  detectProviderFromModelId,
  getProviderColor,
  formatPublicSystemReference,
  getPublicSystemName,
  humanizeAppSlug,
  resolveEffortCodeWordForDisplay,
} from '../../config/providerCodeNames';
import { listActivityEvents, type ActivityEvent } from '../../services/rbacService';
import { COST_ESTIMATE_TITLE, costEstimateMarker, friendlySourceLabel, friendlySummary, generationStageLabel } from '../../config/usageVocabulary';
import { capDistinctSystems, mergeAuxEventsIntoParents } from './eventRowDisplay';
// Round 10 (Miles 2026-08-13): every value is click-to-copy via the page's own
// CopyText — plain text inheriting the cell's type, a quiet "copied" pill on
// click, no icons or popouts (the wallet components clipped names and added a
// phantom scroll here — see CopyText). stopPropagation, so copying never
// toggles a row.
import CopyText from './CopyText';
import {
  ACTIVITY_COLORS,
  CARD_STYLE,
  formatDurationMs,
  formatUsd,
  formatWu,
  formatWuDetail,
  wuFromUsd,
  WU_LABEL,
} from './activityTheme';

const Panel = styled.section`
  ${CARD_STYLE}
  padding: 1.25rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--brand-text);
  margin: 0;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const Select = styled.select`
  font-size: 0.75rem;
  padding: 0.3125rem 0.5rem;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 6px;
  background: ${ACTIVITY_COLORS.surface};
  color: ${ACTIVITY_COLORS.ink};
  cursor: pointer;
`;

const GhostButton = styled.button`
  font-size: 0.75rem;
  padding: 0.3125rem 0.75rem;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 6px;
  background: transparent;
  color: ${ACTIVITY_COLORS.inkSecondary};
  cursor: pointer;
  text-transform: lowercase;

  &:hover:not(:disabled) {
    background: ${ACTIVITY_COLORS.gridline};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/* Wide tables scroll inside their own container — the page never scrolls
   horizontally. */
const Scroller = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: 0.8125rem;

  th {
    text-align: left;
    font-weight: 500;
    color: ${ACTIVITY_COLORS.inkSecondary};
    padding: 0 0.75rem 0.5rem 0;
    border-bottom: 1px solid ${ACTIVITY_COLORS.border};
    text-transform: lowercase;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  td {
    padding: 0.5rem 0.75rem 0.5rem 0;
    border-bottom: 1px solid ${ACTIVITY_COLORS.gridline};
    color: ${ACTIVITY_COLORS.ink};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  td.numeric,
  th.numeric {
    text-align: right;
  }

  tr:last-child td {
    border-bottom: none;
  }

  /* Phones: collapse to the columns that answer "who ran what and what it
     used" — timing stays one tap away in the row expansion, so nothing is
     lost, and the table stops needing a sideways scroll just to read it. */
  @media (max-width: 719px) {
    min-width: 0;

    th.m-hide,
    td.m-hide {
      display: none;
    }
  }
`;

const Muted = styled.span`
  color: ${ACTIVITY_COLORS.inkMuted};
`;

const TestBadge = styled.span`
  font-size: 0.6875rem;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 4px;
  padding: 0.0625rem 0.3125rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  margin-left: 0.375rem;
`;

/* Expandable per-request detail (round 2 granularity) */
const DetailRow = styled.tr`
  background: #fbfbfa;
`;

const DetailCell = styled.td`
  padding: 0.75rem 1rem !important;
  white-space: normal !important;
`;

/* An obvious way OUT of the expansion (2026-08-16 Miles) — the row click
   still toggles, but a visible control beats knowing that. Own row above the
   grid so it can never overlap the detail columns. */
const DetailCloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: -0.25rem 0 0.375rem;
`;

const DetailClose = styled.button`
  border: 1px solid ${ACTIVITY_COLORS.border};
  background: transparent;
  color: ${ACTIVITY_COLORS.inkMuted};
  border-radius: 4px;
  font-size: 0.6875rem;
  padding: 0.125rem 0.4375rem;
  cursor: pointer;
  text-transform: lowercase;
  font-family: inherit;

  &:hover {
    background: ${ACTIVITY_COLORS.gridline};
    color: ${ACTIVITY_COLORS.ink};
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.625rem 1.5rem;
  font-size: 0.75rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  span {
    color: ${ACTIVITY_COLORS.inkMuted};
    text-transform: lowercase;
    font-size: 0.6875rem;
  }

  b {
    font-weight: 500;
    color: ${ACTIVITY_COLORS.ink};
    font-variant-numeric: tabular-nums;
    word-break: break-all;
  }
`;

const ProviderDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  margin-right: 0.375rem;
`;

/* Many-system turns wrap inside the expansion: flex children wrap at item
   boundaries even with no whitespace between them, which adjacent nowrap
   spans never did — they overflowed the grid track and slid under the next
   column. Each system stays an unbreakable dot+name unit. */
const SystemWrapList = styled.b`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.25rem;
  row-gap: 0.1875rem;
`;

const ExpandableRow = styled.tr`
  cursor: pointer;

  &:hover td {
    background: var(--brand-canvas);
  }
`;

/* R18: per-call breakdown inside the expanded request — same series as the
   chat ⓘ popup ("N model calls · in call order"), so the two reconcile. */
const CallBreakdown = styled.div`
  margin-top: 0.875rem;
  border-top: 1px solid ${ACTIVITY_COLORS.border};
  padding-top: 0.625rem;
  /* the per-call table keeps its own sideways scroll on phones */
  overflow-x: auto;

  table {
    width: auto;
    min-width: 280px;
    border-collapse: collapse;
    font-size: 0.75rem;
  }
  th {
    text-align: left;
    font-weight: 500;
    color: ${ACTIVITY_COLORS.inkMuted};
    text-transform: lowercase;
    font-size: 0.6875rem;
    padding: 0 1rem 0.25rem 0;
    border-bottom: none;
  }
  td {
    padding: 0.25rem 1rem 0.25rem 0;
    border-bottom: none;
    color: ${ACTIVITY_COLORS.ink};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  th.numeric,
  td.numeric {
    text-align: right;
  }
`;

const CallBreakdownTitle = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${ACTIVITY_COLORS.inkMuted};
  text-transform: lowercase;
  margin-bottom: 0.375rem;
`;

const StateNote = styled.div`
  color: ${ACTIVITY_COLORS.inkMuted};
  font-size: 0.8125rem;
  padding: 1.5rem 0;
  text-align: center;
`;

/* R17 cold-load rows: a table-shaped shimmer instead of one bare text line,
   so "loading" never reads as "no data". */
const SkeletonRow = styled.div<{ $w?: string }>`
  height: 1rem;
  width: ${(p) => p.$w || '100%'};
  border-radius: 4px;
  margin: 0.625rem 0;
  background: linear-gradient(
    90deg,
    ${ACTIVITY_COLORS.gridline} 25%,
    #f4f3ef 37%,
    ${ACTIVITY_COLORS.gridline} 63%
  );
  background-size: 400% 100%;
  animation: prest0n-activity-shimmer 1.4s ease infinite;

  @keyframes prest0n-activity-shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

/* Pager renders twice — above the table (owner placement: paging lives at the
   top) and again below it, because after 500 rows nobody should scroll back
   up just to turn the page. */
const PagerBar = styled.div<{ $bottom?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  ${(p) => (p.$bottom ? 'margin-top: 1rem;' : 'margin-bottom: 0.875rem;')}
`;

function formatWhen(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  // Second precision (2026-08-16 Miles): tasks land seconds apart, and a
  // minute-only stamp made same-minute rows read as simultaneous.
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(
    undefined,
    { hour: '2-digit', minute: '2-digit', second: '2-digit' }
  )}`;
}

interface Props {
  /** R23: view another organization's events (Prest0 Inc. cross-org viewer);
      server-authorized — omitted for the normal own-workspace view. */
  accountId?: string | null;
  memberId?: string | null;
  /** scope to a role and everything beneath it (R19); server-validated */
  roleId?: string | null;
  memberNames?: Record<string, string>;
  showMemberColumn: boolean;
  /** the window every fetch (and csv export) covers; endMs null = "to now" */
  startMs: number;
  endMs?: number | null;
  /** raw model ids present in the range (labels resolved to station names here) */
  modelIds: string[];
  /** bump to re-fetch (round 12 refresh button) without losing filter state */
  refreshNonce?: number;
}

/** How many requests per page — user-selectable (50 default). */
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];
/** Hard ceiling for a CSV export: 50 server pages x 200 rows. */
const EXPORT_MAX_PAGES = 50;
const EXPORT_PAGE_SIZE = 200;

interface PageCursor {
  cursorMs: number;
  cursorId: string | null;
}

/**
 * Excel/Sheets treat a leading =, +, -, or @ as a formula. Text fields (member
 * names, sources, session ids) are attacker-influencable, so neutralize them;
 * numeric columns are locally formatted numbers and stay untouched.
 */

/** Standalone app-tool rows: humanized app name from the row (legacy rows
 *  lack the app: model id — fall back to the summary's first token). */
function appNameFromSummary(summary?: string | null): string {
  const first = String(summary || '').split('·')[0].trim().toLowerCase();
  const slug = first === 'app tool call'
    ? String(summary || '').split('·')[1]?.trim().toLowerCase() || 'app'
    : first || 'app';
  return humanizeAppSlug(slug);
}

function guardCsvText(value: string): string {
  return /^[=+\-@]/.test(value) ? `'${value}` : value;
}

const ActivityEventsTable: React.FC<Props> = ({
  accountId,
  memberId,
  roleId,
  memberNames,
  showMemberColumn,
  startMs,
  endMs,
  modelIds,
  refreshNonce,
}) => {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** selected STATION name ('' = all systems) — resolved to raw ids per request */
  const [modelFilter, setModelFilter] = useState<string>('');
  // P3 (2026-09-16): '' = everything; PREST0N_OWN_WORK_FILTER = only the work
  // Prest0n did on his own; 'user-turn' = only what a person asked for.
  const [kindFilter, setKindFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cursors, setCursors] = useState<PageCursor[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<PageCursor | null>(null);
  const [exporting, setExporting] = useState(false);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  // Round-16: which row's "app tool call" ⓘ explainer is open (one at a time).

  // White-label mandate: the dropdown shows versionless STATION names, never
  // raw ids or versioned codenames. The station map is many-to-one — whole
  // internal families can share a display name (legacy spark + titan → Titan)
  // — so ALL raw ids sharing a station merge into ONE entry that filters
  // across every one of them.
  const modelOptions = useMemo(() => {
    const byLabel = new Map<string, string[]>();
    for (const id of modelIds) {
      const label = getPublicSystemName(id);
      const ids = byLabel.get(label) ?? [];
      ids.push(id);
      byLabel.set(label, ids);
    }
    return Array.from(byLabel.entries())
      .map(([label, ids]) => ({ label, ids }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [modelIds]);

  // The raw ids behind the selected station (undefined = no filter).
  const filteredModelIds = useMemo(() => {
    if (!modelFilter) return undefined;
    return modelOptions.find((m) => m.label === modelFilter)?.ids;
  }, [modelFilter, modelOptions]);

  const load = useCallback(
    async (cursor?: PageCursor) => {
      setLoading(true);
      setError(null);
      try {
        const res = await listActivityEvents({
          accountId: accountId || undefined,
          memberId: memberId || undefined,
          roleId: roleId || undefined,
          startMs,
          endMs: endMs ?? undefined,
          modelIds: filteredModelIds,
          kinds: kindFilter ? [kindFilter] : undefined,
          pageSize,
          cursorMs: cursor?.cursorMs,
          // Compound (timestamp, docId) cursor: a timestamp-only cursor
          // dropped rows sharing the boundary millisecond across pages.
          cursorId: cursor?.cursorId ?? undefined,
        });
        // Legacy standalone memory-update rows fold into their parent task
        // row (new events fold server-side and never mint them).
        setEvents(mergeAuxEventsIntoParents(res.events));
        setHasMore(res.hasMore);
        setNextCursor(
          res.hasMore && res.nextCursorMs !== null
            ? { cursorMs: res.nextCursorMs, cursorId: res.nextCursorId ?? null }
            : null
        );
      } catch (err: any) {
        setError(err?.message || 'could not load activity right now.');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId, memberId, roleId, startMs, endMs, filteredModelIds, kindFilter, pageSize, refreshNonce]
  );

  useEffect(() => {
    setCursors([]);
    setExpandedId(null);
    load();
  }, [load]);

  const eventToCsvRow = useCallback(
    (e: ActivityEvent) => [
      e.timestampMs ? new Date(e.timestampMs).toISOString() : '',
      ...(showMemberColumn
        ? [guardCsvText(memberNames?.[e.memberId ?? ''] || e.memberId || '')]
        : []),
      // White-label: the export carries versionless station names, never raw
      // vendor ids or versioned codenames.
      guardCsvText(getPublicSystemName(e.modelId || '')),
      // WU is display-derived from the user-charged USD (Standard Rate) —
      // same derivation as the on-screen column and the chat ⓘ popup.
      wuFromUsd(e.amountUsd).toFixed(6),
      e.durationMs ? String(e.durationMs) : '',
      e.amountUsd.toFixed(6),
      // R18: every model call of the request, in call order — same series as
      // the chat ⓘ popup, so exports reconcile against it too. Per-call WU
      // only when that generation carries its own USD; station alone otherwise.
      guardCsvText(
        (e.generations ?? [])
          .map((g) =>
            g.amountUsd > 0
              ? `${formatPublicSystemReference(g.model, g.effort)}:${formatWuDetail(wuFromUsd(g.amountUsd))}pr0`
              : formatPublicSystemReference(g.model, g.effort)
          )
          .join(' | ')
      ),
    ],
    [showMemberColumn, memberNames]
  );

  // Exports the COMPLETE filtered result set by walking the server cursor —
  // not just the page on screen. Bounded by EXPORT_MAX_PAGES (10k rows).
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // 2026-08-13 WU contract: no token columns, no cost_pr0 — Work Units
      // are the single usage number, dollars stay (dollars are not tokens).
      const header = [
        'when',
        ...(showMemberColumn ? ['member'] : []),
        'system',
        'pr0',
        'duration_ms',
        'cost_usd',
        'system_steps_in_order',
      ];
      const rows: string[][] = [];
      let cursor: PageCursor | undefined;
      let truncated = false;
      for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
        const res = await listActivityEvents({
          accountId: accountId || undefined,
          memberId: memberId || undefined,
          roleId: roleId || undefined,
          startMs,
          endMs: endMs ?? undefined,
          modelIds: filteredModelIds,
          pageSize: EXPORT_PAGE_SIZE,
          cursorMs: cursor?.cursorMs,
          cursorId: cursor?.cursorId ?? undefined,
        });
        for (const e of res.events) rows.push(eventToCsvRow(e));
        if (!res.hasMore || res.nextCursorMs === null) break;
        cursor = { cursorMs: res.nextCursorMs, cursorId: res.nextCursorId ?? null };
        if (page === EXPORT_MAX_PAGES - 1) truncated = true;
      }
      const lines = [header, ...rows]
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const csv = truncated
        ? `${lines}\n"export truncated at ${rows.length} rows — narrow the date range for the rest"`
        : lines;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* the button re-enables; the on-screen table is unaffected */
    } finally {
      setExporting(false);
    }
  };

  const pager = (position: 'top' | 'bottom') => (
    <PagerBar $bottom={position === 'bottom'}>
      <GhostButton
        disabled={loading || cursors.length === 0}
        onClick={() => {
          const next = [...cursors];
          next.pop();
          setCursors(next);
          load(next[next.length - 1]);
        }}
      >
        ← newer
      </GhostButton>
      <Muted style={{ fontSize: '0.75rem' }}>
        showing {events.length} request{events.length === 1 ? '' : 's'}
        {cursors.length > 0 ? ` · page ${cursors.length + 1}` : ''}
      </Muted>
      <GhostButton
        disabled={loading || !hasMore || nextCursor === null}
        onClick={() => {
          if (nextCursor === null) return;
          setCursors([...cursors, nextCursor]);
          load(nextCursor);
        }}
      >
        older →
      </GhostButton>
    </PagerBar>
  );

  return (
    <Panel>
      <Head>
        <Title>activity</Title>
        <Filters>
          <Select
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            aria-label="filter by system"
          >
            <option value="">all systems</option>
            {modelOptions.map((m) => (
              <option key={m.label} value={m.label}>
                {m.label}
              </option>
            ))}
          </Select>
          <Select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            aria-label="filter by who asked for the work"
            data-activity-kind-filter=""
          >
            <option value="">all work</option>
            <option value={PREST0N_OWN_WORK_FILTER}>Prest0n&apos;s own work</option>
            <option value="user-turn">asked for by a person</option>
          </Select>
          <Select
            value={String(pageSize)}
            onChange={(e) => setPageSize(Number(e.target.value) || PAGE_SIZE_OPTIONS[0])}
            aria-label="requests per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} per page
              </option>
            ))}
          </Select>
          <GhostButton onClick={exportCsv} disabled={events.length === 0 || exporting}>
            {exporting ? 'exporting…' : 'export csv'}
          </GhostButton>
        </Filters>
      </Head>

      {/* R17 (Miles: never look empty when there is data): a refetch behind
          already-rendered rows keeps them on screen, dimmed under an
          "updating…" note, instead of wiping the table to one text line for
          the duration. Only a load with NOTHING rendered yet shows the
          skeleton rows. */}
      {loading && events.length === 0 && (
        <div aria-label="loading activity">
          <SkeletonRow $w="100%" />
          <SkeletonRow $w="92%" />
          <SkeletonRow $w="97%" />
          <SkeletonRow $w="88%" />
        </div>
      )}
      {loading && events.length > 0 && <StateNote>updating…</StateNote>}
      {!loading && error && <StateNote>{error}</StateNote>}
      {/* Terminal empty state only when there is genuinely nothing to page
          through — and never while a fetch is running. A filtered scan can
          return an empty page WITH more feed left (hasMore) — that gets the
          pager below, not a dead end. */}
      {!loading && !error && events.length === 0 && !hasMore && cursors.length === 0 && (
        <StateNote>no activity recorded in this period.</StateNote>
      )}
      {!loading && !error && events.length === 0 && (hasMore || cursors.length > 0) && (
        <StateNote>
          no matching requests in this stretch — use “older” to keep looking.
        </StateNote>
      )}

      {!error && (events.length > 0 || (!loading && (hasMore || cursors.length > 0))) && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s ease' }}>
          {pager('top')}
          {events.length > 0 && (
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <th>when</th>
                  {showMemberColumn && <th>member</th>}
                  <th>system</th>
                  <th className="numeric">{WU_LABEL.toLowerCase()}</th>
                  <th className="numeric m-hide">time</th>
                  <th className="numeric">cost ($)</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  // when, system, wu, time, cost (+member) — source lives in
                  // the expansion, not a column
                  const colCount = 5 + (showMemberColumn ? 1 : 0);
                  const expanded = expandedId === e.eventId;
                  // Distinct systems of the turn in first-use order — shared
                  // by the collapsed cell (capped) and the expansion (full,
                  // wrapped).
                  const distinctSystems: { model: string; name: string }[] = [];
                  for (const g of e.generations ?? []) {
                    const name = getPublicSystemName(g.model);
                    if (!distinctSystems.some((d) => d.name === name)) {
                      distinctSystems.push({ model: g.model, name });
                    }
                  }
                  return (
                    <React.Fragment key={e.eventId}>
                      <ExpandableRow
                        onClick={() => setExpandedId(expanded ? null : e.eventId)}
                        aria-expanded={expanded}
                      >
                        <td>
                          <CopyText>{formatWhen(e.timestampMs)}</CopyText>
                          {e.testMode && <TestBadge>test</TestBadge>}
                        </td>
                        {showMemberColumn && (
                          <td>
                            {/* P3: Prest0n's own work is HIS, however the row's
                                memberId reads — the acting uid on a check-in is
                                the agent's, which rendered as a raw id nobody
                                could identify. */}
                            {!isPersonsOwnTurn(e) ? (
                              <CopyText copyValue={e.memberId || ''}>Prest0n</CopyText>
                            ) : memberNames?.[e.memberId ?? ''] ? (
                              <CopyText>{memberNames[e.memberId ?? '']}</CopyText>
                            ) : e.memberId ? (
                              /* No profile name — friendly short id; click
                                 copies the FULL id (the expansion shows it
                                 whole too). */
                              <Muted>
                                <CopyText copyValue={e.memberId}>
                                  {e.memberId.length > 14
                                    ? `${e.memberId.slice(0, 8)}…${e.memberId.slice(-4)}`
                                    : e.memberId}
                                </CopyText>
                              </Muted>
                            ) : (
                              <Muted>unknown</Muted>
                            )}
                          </td>
                        )}
                        <td>
                          {/* 2026-08-15 (Miles): the collapsed row shows EVERY
                              distinct system of the turn in first-use order —
                              a "prism slate · 3 calls" headline hid that call
                              1 was pulse rapids until expanded. Single-system
                              turns render exactly as before. */}
                          {(() => {
                            // App-tool rows (2026-08-16 Miles): the SYSTEM is
                            // the APPLICATION, humanized ("google calendar"),
                            // rendered exactly like model rows — same dot,
                            // same spacing, NO explainer icon. Standalone
                            // rows only; in-task app calls live under their
                            // task's row as steps.
                            if (e.source === 'app-tool-call') {
                              return (
                                <span style={{ whiteSpace: 'nowrap' }}>
                                  <ProviderDot $color={getProviderColor('composio')} />
                                  <CopyText>{getPublicSystemName(e.modelId || '') !== 'system'
                                    ? getPublicSystemName(e.modelId || '')
                                    : appNameFromSummary(e.summary)}</CopyText>
                                </span>
                              );
                            }
                            const distinct = distinctSystems;
                            if (distinct.length <= 1) {
                              return (
                                <>
                                  <ProviderDot $color={getProviderColor(e.provider || undefined)} />
                                  <CopyText>{getPublicSystemName(e.modelId || '')}</CopyText>
                                </>
                              );
                            }
                            /* Collapsed rows stay one line: up to 5 systems
                               render in full; past that, the first 4 plus a
                               "+K more" marker (dropping to 4 leaves the
                               marker room). The expansion always carries the
                               complete wrapped list one click away. */
                            const { shown, hiddenCount } = capDistinctSystems(distinct);
                            return (
                              <>
                                {shown.map((d, di) => (
                                  <span key={d.name} style={{ whiteSpace: 'nowrap' }}>
                                    {di > 0 && <Muted as="span">{' + '}</Muted>}
                                    <ProviderDot $color={getProviderColor(detectProviderFromModelId(d.model))} />
                                    <CopyText>{d.name}</CopyText>
                                  </span>
                                ))}
                                {hiddenCount > 0 && (
                                  <Muted as="span" title="open the row for the full system list">
                                    {` + ${hiddenCount} more`}
                                  </Muted>
                                )}
                              </>
                            );
                          })()}
                          {/* R18: a multi-call request says so up front — the
                              full per-call series (mirroring the chat ⓘ) is in
                              the expansion. */}
                          {(e.generations?.length ?? 0) > 1 && (
                            <TestBadge as="span">{e.generations!.length} steps</TestBadge>
                          )}
                        </td>
                        {/* The single usage number: turn WU, derived from the
                            same user-charged USD the chat ⓘ popup derives
                            from — the two surfaces reconcile by construction.
                            Plain single-line copyable (round 9): the wallet's
                            stacked value+unit display doubled every row's
                            height and repeated "pr0" down the column the
                            header already names; USD sits one cell over. */}
                        <td className="numeric">
                          <CopyText>{formatWuDetail(wuFromUsd(e.amountUsd))}</CopyText>
                        </td>
                        <td className="numeric m-hide">
                          {e.durationMs ? (
                            <CopyText>{formatDurationMs(e.durationMs)}</CopyText>
                          ) : (
                            <Muted>—</Muted>
                          )}
                        </td>
                        <td className="numeric">
                          <CopyText>{formatUsd(e.amountUsd)}</CopyText>
                        </td>
                      </ExpandableRow>
                      {expanded && (
                        <DetailRow>
                          <DetailCell colSpan={colCount}>
                            <DetailCloseRow>
                              <DetailClose
                                type="button"
                                aria-label="close task details"
                                onClick={() => setExpandedId(null)}
                              >
                                close ×
                              </DetailClose>
                            </DetailCloseRow>
                            <DetailGrid>
                              <DetailItem
                                style={
                                  /* Long system lists take the full grid row
                                     so the wrapped list breathes instead of
                                     stacking inside one narrow track. */
                                  distinctSystems.length > 3
                                    ? { gridColumn: '1 / -1' }
                                    : undefined
                                }
                              >
                                {/* 2026-08-15 (Miles): "system", not
                                    "provider" — and the FULL distinct system
                                    list of the turn, not the headline family.
                                    Generalized field: no effort codes here
                                    (the per-call list below carries them). */}
                                <span>system</span>
                                <SystemWrapList>
                                  {(() => {
                                    const distinct = distinctSystems;
                                    if (distinct.length === 0) {
                                      return (
                                        <span style={{ whiteSpace: 'nowrap' }}>
                                          <ProviderDot $color={getProviderColor(e.provider || undefined)} />
                                          {getPublicSystemName(e.modelId || '')}
                                        </span>
                                      );
                                    }
                                    return distinct.map((d, di) => (
                                      <span key={d.name} style={{ whiteSpace: 'nowrap' }}>
                                        {di > 0 && '+ '}
                                        <ProviderDot $color={getProviderColor(detectProviderFromModelId(d.model))} />
                                        {d.name}
                                      </span>
                                    ));
                                  })()}
                                </SystemWrapList>
                              </DetailItem>
                              {/* Turn WU — same USD/2.18 derivation as the
                                  table column and the chat ⓘ popup. */}
                              <DetailItem>
                                <span>work units</span>
                                <b>
                                  <CopyText>{`${formatWuDetail(wuFromUsd(e.amountUsd))} ${WU_LABEL}`}</CopyText>
                                </b>
                              </DetailItem>
                              {/* Every column the phone layout hides is
                                  recoverable here — source included, not just
                                  timing. */}
                              <DetailItem>
                                <span>source</span>
                                <b style={{ fontWeight: 400 }}>
                                  <CopyText>{friendlySourceLabel(e.source)}</CopyText>
                                </b>
                              </DetailItem>
                              <DetailItem>
                                <span>task time</span>
                                <b>
                                  {e.durationMs ? (
                                    <CopyText>{formatDurationMs(e.durationMs)}</CopyText>
                                  ) : (
                                    '—'
                                  )}
                                  {/* R20: background work (memory update,
                                      follow-ups) is tracked on the task's own
                                      row — shown, never counted into the
                                      task's time. */}
                                  {Boolean(e.auxDurationMs) && (
                                    <Muted as="span">
                                      {` · +${formatDurationMs(e.auxDurationMs as number)} background`}
                                    </Muted>
                                  )}
                                </b>
                              </DetailItem>
                              {e.sessionId && (
                                <DetailItem>
                                  <span>session</span>
                                  <b>
                                    <CopyText>{e.sessionId}</CopyText>
                                  </b>
                                </DetailItem>
                              )}
                              {/* Long ids never dead-end: shown WHOLE here
                                  (word-break wraps them) and one click
                                  copies. Member id lives here too now that
                                  the row shows only the name (round 10). */}
                              {e.memberId && (
                                <DetailItem>
                                  <span>member id</span>
                                  <b>
                                    <CopyText>{e.memberId}</CopyText>
                                  </b>
                                </DetailItem>
                              )}
                              <DetailItem>
                                <span>request id</span>
                                <b>
                                  <CopyText>{e.eventId}</CopyText>
                                </b>
                              </DetailItem>
                              {e.summary && (
                                <DetailItem style={{ gridColumn: '1 / -1' }}>
                                  <span>summary</span>
                                  <b style={{ fontWeight: 400 }}>
                                    <CopyText>{friendlySummary(e.summary)}</CopyText>
                                  </b>
                                </DetailItem>
                              )}
                            </DetailGrid>
                            {/* R18 (owner directive 2026-08-09): the SAME
                                per-call series the chat ⓘ popup shows, in call
                                order, so the two surfaces reconcile line by
                                line — a request is one row above, its model
                                calls are itemized here. */}
                            {(e.generations?.length ?? 0) > 0 && (
                              <CallBreakdown>
                                <CallBreakdownTitle>
                                  {e.generations!.length} system step
                                  {e.generations!.length === 1 ? '' : 's'} · in order
                                </CallBreakdownTitle>
                                <table>
                                  <thead>
                                    <tr>
                                      <th>call</th>
                                      <th>system</th>
                                      <th className="numeric">{WU_LABEL.toLowerCase()}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {e.generations!.map((g, gi) => (
                                      <tr key={`${e.eventId}_g${gi}`}>
                                        <td>{gi + 1}</td>
                                        <td>
                                          <ProviderDot
                                            $color={getProviderColor(detectProviderFromModelId(g.model))}
                                          />
                                          {getPublicSystemName(g.model)}
                                          {/* 2026-08-16 (Miles): the effort code
                                              word always displays — stored effort
                                              wins, else the SSOT level for the
                                              step's model (display-time fallback,
                                              lockstep with the chat ⓘ popups). */}
                                          {resolveEffortCodeWordForDisplay(g.model, g.effort) && (
                                            <Muted as="span">{` · ${resolveEffortCodeWordForDisplay(g.model, g.effort)}`}</Muted>
                                          )}
                                          {/* Thread-ledger PR-5: background steps say WHAT
                                              they were — same label the ⓘ shows, so the two
                                              surfaces stay uniform (owner requirement). */}
                                          {generationStageLabel(g.stage) && (
                                            <Muted as="span">{` · ${generationStageLabel(g.stage)}`}</Muted>
                                          )}
                                        </td>
                                        {/* Per-call WU only when the generation
                                            carries its own USD; otherwise the
                                            station stands alone. */}
                                        <td className="numeric">
                                          {g.amountUsd > 0 ? (
                                            <>
                                              {/* 2026-09-08: unit-priced media renders are estimates — same marker as the ⓘ. */}
                                              {costEstimateMarker(g) && (
                                                <span data-cost-estimate="" title={COST_ESTIMATE_TITLE} aria-label="estimated">{`${costEstimateMarker(g)} `}</span>
                                              )}
                                              {formatWuDetail(wuFromUsd(g.amountUsd))}
                                            </>
                                          ) : (
                                            <Muted>—</Muted>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </CallBreakdown>
                            )}
                          </DetailCell>
                        </DetailRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
          )}

          {pager('bottom')}
        </div>
      )}
    </Panel>
  );
};

export default ActivityEventsTable;
